import React, { useEffect, useRef, useState } from 'react'
import { Provider as ReduxProvider } from 'react-redux'
import { ActionSheetProvider } from '@expo/react-native-action-sheet'
import { initialWindowMetrics, SafeAreaProvider } from 'react-native-safe-area-context'
import { StripeProvider } from '@stripe/stripe-react-native'
import { GestureHandlerRootView } from 'react-native-gesture-handler'
import { KeyboardProvider, KeyboardAvoidingView } from 'react-native-keyboard-controller'
import { SystemBars } from 'react-native-edge-to-edge'
import type { SystemBarsProps } from 'react-native-edge-to-edge'

import Config from 'APP/Config'
import RootContainer from 'APP/Containers/RootContainer'
import { useDispatch, useSelector } from 'APP/Hooks/redux'
import store from 'APP/Store'
import StartupActions from 'APP/Redux/StartupRedux'

import { NavProvider } from 'APP/Nav/Provider'
import AppThemeProvider from 'APP/Theme/AppThemeProvider'
import DevToolsProvider from 'APP/Lib/DevTools'
import { PortalProvider } from 'APP/Helpers/Portal'
import { setIsHeadlessTask } from 'APP/Lib/HeadlessTask'
import { usePrivacyMask } from 'APP/Hooks/usePrivacyMask'
import { brownfieldActions } from 'APP/Store/Brownfield'
import { ErrorBoundary } from 'APP/Components/ErrorBoundary'
import { ZendeskWidget } from 'APP/Services/Zendesk/Zendesk.web'
import { isWeb } from 'APP/Helpers/checkPlatform'
import { ColourScheme } from 'APP/Helpers/AppearanceUtils'
import { initZendesk } from 'APP/Services/Zendesk/Zendesk'
import { MattermostManager } from 'APP/Components/MattermostManager'
import UserInteractionWatcher from 'APP/Components/UserInteractionWatcher'
import VideoCallSSEListener from 'APP/Components/VideoCallSSEListener'
import { isEmbeddedEnvironment } from 'APP/BrownfieldBridge/utils'

const style = { flex: 1 }

// Keeps Android nav bar icons visible when the app forces a light colour scheme.
const systemBarsStyle: SystemBarsProps['style'] = {
  navigationBar: Config.APP_COLOUR_SCHEME === ColourScheme.LIGHT ? 'dark' : 'auto',
}

export const AppContent = () => {
  const dispatch = useDispatch()
  const initialized = useSelector((state) => state.initialization?.initialized)
  const [isNavigationReady, setIsNavigationReady] = useState(false)
  const [mountZendesk, setMountZendesk] = useState(false)

  useEffect(() => {
    if (initialized) {
      setIsHeadlessTask(false)
      dispatch(StartupActions.startup())
    }
  }, [initialized, dispatch])

  // Clear navigation readiness when AppContent unmounts so a subsequent mount
  // starts from a clean slate and waitForNavigationReady blocks until the new
  // NavigationContainer fires onReady again.
  useEffect(() => {
    return () => {
      dispatch(StartupActions.resetNavigationReady())
    }
  }, [dispatch])

  useEffect(() => {
    if (isWeb()) {
      requestAnimationFrame(() => setMountZendesk(true))
    } else {
      initZendesk()
    }
  }, [])

  const onNavigationReady = () => {
    setIsNavigationReady(true)
    dispatch(StartupActions.setNavigationReady())
  }

  if (!initialized) {
    return null
  }

  return (
    <>
      {mountZendesk && <ZendeskWidget />}
      <NavProvider onReady={onNavigationReady}>
        <UserInteractionWatcher>
          <PortalProvider>
            <MattermostManager />
            <VideoCallSSEListener />
            <RootContainer isNavigationReady={isNavigationReady} />
          </PortalProvider>
        </UserInteractionWatcher>
      </NavProvider>
    </>
  )
}

export interface AppProps {
  /**
   * Embedded VoIP cold-start auth + host-return gate, sourced from native initial props.
   * Omit (or pass `undefined`) to leave the existing value untouched — RN can deliver
   * partial prop updates and a flipped `false` would wrongly clear a cold-VoIP true and
   * break incoming / preload.
   */
  skipTokenExchange?: boolean
}

export const App = ({ skipTokenExchange }: AppProps) => {
  // Mirror skipTokenExchange from native initial props into the brownfield slice. It controls
  // embedded auth (VoIP cold preload / exchange skip) and gates the end-of-call host-return
  // path in dismissVideoCallScreen.
  //
  // This must happen during render (not in a useEffect) so the value is committed to the
  // Redux store before the startup saga runs and reads it. A useEffect would fire after the
  // first render cycle, too late for the saga to see the correct initial value.
  //
  // Wrapped in isEmbeddedEnvironment() because this is standalone-irrelevant state — in
  // standalone mode skipTokenExchange is always undefined and the block never executes,
  // but the guard makes the intent explicit.
  const lastSkipTokenExchange = useRef<boolean | undefined>(undefined)
  if (
    isEmbeddedEnvironment() &&
    skipTokenExchange !== undefined &&
    skipTokenExchange !== lastSkipTokenExchange.current
  ) {
    lastSkipTokenExchange.current = skipTokenExchange
    store.dispatch(brownfieldActions.setSkipTokenExchange(skipTokenExchange))
  }

  // Add privacy attributes to all img and svg elements (web only)
  usePrivacyMask()

  return (
    <ErrorBoundary>
      <SystemBars style={systemBarsStyle} />
      <SafeAreaProvider initialMetrics={initialWindowMetrics}>
        <KeyboardProvider preserveEdgeToEdge navigationBarTranslucent statusBarTranslucent>
          <KeyboardAvoidingView style={style} behavior="padding">
            <GestureHandlerRootView style={style}>
              <StripeProvider publishableKey={Config.STRIPE_PUBLIC_KEY}>
                <ActionSheetProvider>
                  <AppThemeProvider>
                    <DevToolsProvider>
                      <ReduxProvider store={store}>
                        <AppContent />
                      </ReduxProvider>
                    </DevToolsProvider>
                  </AppThemeProvider>
                </ActionSheetProvider>
              </StripeProvider>
            </GestureHandlerRootView>
          </KeyboardAvoidingView>
        </KeyboardProvider>
      </SafeAreaProvider>
    </ErrorBoundary>
  )
}

export default App
